import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreatePostDraftResponse } from 'api';
import { Goal, User } from 'types';

import { RefetchGoalDetails } from '../../types';
import { GoalFormFields } from './GoalFormFields';
import { useCreateGoalForm } from './hooks/useCreateGoalForm';
import { useEditGoalForm } from './hooks/useEditGoalForm';

type CreateGoalFormProps = {
  isEdit?: false;
  draft: CreatePostDraftResponse;
  usersList: User[];
  achievementId?: string;
  employeeId?: string;
  onClose?: () => void;
};

type EditGoalFormProps = {
  isEdit: true;
  data?: Goal;
  usersList: User[];
  isDetailsLoaded: boolean;
  handleRemoveClick?: () => void;
  refetchGoal: RefetchGoalDetails;
};

export type GoalFormProps = CreateGoalFormProps | EditGoalFormProps;

export function GoalForm(props: GoalFormProps): ReactElement {
  if (props.isEdit) return <EditGoalContent {...props} />;
  return <CreateGoalContent {...props} />;
}

function CreateGoalContent({
  draft,
  usersList,
  achievementId,
  employeeId,
  onClose,
}: CreateGoalFormProps): ReactElement {
  const {
    control,
    handleFormSubmit,
    handleUploadAll,
    attachedImages,
    attachedDocs,
    attachedVideos,
    handleDeleteAttachment,
    statusOptions,
    assigneeOptions,
    isLoading,
    isOpen,
    progress,
    handleSubmitConfirmModal,
    handleCloseConfirmModal,
    handleAssigneeChange,
    goBack,
  } = useCreateGoalForm({ usersList, achievementId, employeeId, onClose });

  return (
    <GoalFormFields
      isEdit={false}
      uploadEntityId={draft?.id}
      onCancel={goBack}
      control={control}
      handleFormSubmit={handleFormSubmit}
      handleUploadAll={handleUploadAll}
      attachedImages={attachedImages}
      attachedDocs={attachedDocs}
      attachedVideos={attachedVideos}
      handleDeleteAttachment={handleDeleteAttachment}
      statusOptions={statusOptions}
      assigneeOptions={assigneeOptions}
      isLoading={isLoading}
      isOpen={isOpen}
      progress={progress}
      handleSubmitConfirmModal={handleSubmitConfirmModal}
      handleCloseConfirmModal={handleCloseConfirmModal}
      handleAssigneeChange={handleAssigneeChange}
    />
  );
}

function EditGoalContent({
  data,
  usersList,
  isDetailsLoaded,
  handleRemoveClick,
  refetchGoal,
}: EditGoalFormProps): ReactElement {
  const navigate = useNavigate();
  const {
    control,
    handleFormSubmit,
    handleUploadAll,
    attachedImages,
    attachedDocs,
    attachedVideos,
    handleDeleteAttachment,
    statusOptions,
    assigneeOptions,
    isLoading,
    isOpen,
    progress,
    handleSubmitConfirmModal,
    handleCloseConfirmModal,
    handleAssigneeChange,
    isDirty,
    author,
    createdAt,
    isAssigneeFieldDisabled,
  } = useEditGoalForm({ data, usersList, refetchGoal });

  return (
    <GoalFormFields
      isEdit
      isDetailsLoaded={isDetailsLoaded}
      uploadEntityId={data?.id}
      onCancel={() => navigate(-1)}
      dataEditable={data?.editable}
      handleRemoveClick={handleRemoveClick}
      control={control}
      handleFormSubmit={handleFormSubmit}
      handleUploadAll={handleUploadAll}
      attachedImages={attachedImages}
      attachedDocs={attachedDocs}
      attachedVideos={attachedVideos}
      handleDeleteAttachment={handleDeleteAttachment}
      statusOptions={statusOptions}
      assigneeOptions={assigneeOptions}
      isLoading={isLoading}
      isOpen={isOpen}
      progress={progress}
      handleSubmitConfirmModal={handleSubmitConfirmModal}
      handleCloseConfirmModal={handleCloseConfirmModal}
      handleAssigneeChange={handleAssigneeChange}
      isDirty={isDirty}
      author={author}
      createdAt={createdAt}
      isAssigneeFieldDisabled={isAssigneeFieldDisabled}
    />
  );
}
